"use client";

import Link from "next/link";
import { LOGO_ALT, LOGO_SRC } from "@/lib/brand";

type Props = {
  withText?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "hero";
  href?: string | null;
};

/**
 * Display box (CSS px).
 * We deliberately do NOT use next/image here — the optimizer was serving
 * q=75 tiny srcset widths (32–128px) which made the seal look pixelated.
 * Static PNG + srcSet keeps full fidelity on retina.
 */
const sizes = {
  sm: { box: 48, pad: 5 },
  md: { box: 64, pad: 6 },
  lg: { box: 112, pad: 10 },
  xl: { box: 220, pad: 16 },
  hero: { box: 0, pad: 0 },
};

/** Cache-bust when logo assets change (keep in sync with public/logo files) */
const LOGO_V = "6";

const LOGO_SRCSET = [
  `/logo/seal-256.png?v=${LOGO_V} 256w`,
  `/logo/seal-512.png?v=${LOGO_V} 512w`,
  `/logo/seal-768.png?v=${LOGO_V} 768w`,
  `/logo/seal-1024.png?v=${LOGO_V} 1024w`,
  `/logo/seal-1536.png?v=${LOGO_V} 1536w`,
].join(", ");

const LOGO_FALLBACK = `/logo/seal-1536.png?v=${LOGO_V}`;
const LOGO_DEFAULT = `/logo/seal-768.png?v=${LOGO_V}`;

export function BrandLogo({
  withText = true,
  size = "sm",
  href = "/",
}: Props) {
  const s = sizes[size];
  const isHero = size === "hero";
  /* Display-box attributes for layout reserve — CSS caps actual size (no 1536px blowout) */
  const boxPx = isHero ? 480 : Math.max(s.box * 3, 192);

  const img = (
    // eslint-disable-next-line @next/next/no-img-element -- brand seal must not go through next/image
    <img
      src={isHero ? LOGO_FALLBACK : LOGO_DEFAULT}
      srcSet={LOGO_SRCSET}
      sizes={
        isHero
          ? "(max-height: 800px) min(52vw, 280px), (max-width: 640px) min(78vw, 380px), (max-width: 1100px) min(46vw, 440px), 480px"
          : `${Math.round(s.box * 3)}px`
      }
      alt={LOGO_ALT}
      width={boxPx}
      height={boxPx}
      className="logo-img"
      decoding="async"
      fetchPriority="high"
      draggable={false}
      // Avoid noisy mismatches if an old SW/HMR client briefly holds stale attrs
      suppressHydrationWarning
    />
  );

  const mark = isHero ? (
    <span className="logo-hero">
      <span className="logo-hero-inner">{img}</span>
    </span>
  ) : (
    <span
      className="logo-plate"
      style={{
        width: s.box,
        height: s.box,
        padding: s.pad,
      }}
    >
      <span className="logo-plate-inner">{img}</span>
    </span>
  );

  const text = withText ? (
    <div className="leading-tight min-w-0 text-left">
      <div className="text-[12px] font-semibold tracking-tight text-[var(--text)]">
        Virtuous
      </div>
      <div className="text-[9px] tracking-wide text-[var(--faint)]">
        Trade & Consulting
      </div>
    </div>
  ) : null;

  const inner = (
    <>
      {mark}
      {text}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={
          isHero
            ? "logo-link-hero inline-flex shrink-0 items-center justify-center"
            : "inline-flex shrink-0 items-center gap-2"
        }
      >
        {inner}
      </Link>
    );
  }

  return (
    <div
      className={
        isHero
          ? "inline-flex shrink-0 items-center justify-center"
          : "inline-flex shrink-0 items-center gap-2"
      }
    >
      {inner}
    </div>
  );
}

export { LOGO_SRC, LOGO_FALLBACK };
